// src/components/JobRow.tsx
import React, { useMemo, useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import type { JobRecord } from "../hooks/useJobCenter";
import { STATUS_CONFIG, formatDuration } from "./job-center.config";
import { JOB_KIND_META, STATUS_DISPLAY } from "./job-kind-meta";
import { Download, RefreshCw, Trash2, CalendarRange, Save, Square } from "lucide-react";
import ConfirmationModal from "./ConfirmationModal";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "../lib/supabaseClient";
import { useLeagueSeasonStore } from "@/state/useLeagueSeasonStore";
import { useNotificationContext } from "../contexts/NotificationContext";
import { saveCalendarCandidate } from "../services/seasonTransitionService";
import { ChunkProgressDisplay } from "./ChunkProgressDisplay";
import { humanizeJobError } from "./job-error-messages";
import { useTranslation } from "react-i18next";
import { analyzeCalendar } from "../lib/calendarAnalytics/analyzeCalendar";
import { compactCandidateMetrics } from "../lib/calendarAnalytics/candidateMetrics";
import type { LatLng } from "../lib/geo/haversine";

interface JobRowProps {
  job: JobRecord;
  onRemove: (id: string) => Promise<void> | void;
  onRetry: (id: string) => Promise<void> | void;
  /** « Relancer » proposé (tâche à soi, en échec/annulée, type relançable). */
  canRetry?: boolean;
  onStop?: (id: string) => Promise<void> | void;
  /** Close the Job Center panel (e.g. after navigating to a result). */
  onClose?: () => void;
}

// Resolve team → home coords + wilaya (teams → stadiums → locations), mirroring
// useTeamHomeLocations but as a one-shot for the recovery fallback (no hook, so it
// can be called conditionally). Used to recompute candidate metrics client-side for
// pre-v21 optimize jobs whose result carries no analysis metrics.
async function fetchTeamGeo(teamIds: string[]): Promise<{
  coordsByTeamId: Map<string, LatLng | null>;
  wilayaByTeamId: Map<string, string | null>;
}> {
  const coordsByTeamId = new Map<string, LatLng | null>();
  const wilayaByTeamId = new Map<string, string | null>();
  if (!teamIds.length) return { coordsByTeamId, wilayaByTeamId };

  const { data: teams } = await supabase
    .from("teams")
    .select("id, wilaya, home_stadium_id")
    .in("id", teamIds);
  const stadiumByTeam = new Map<string, string | null>();
  for (const t of teams ?? []) {
    wilayaByTeamId.set(t.id, (t as any).wilaya ?? null);
    stadiumByTeam.set(t.id, (t as any).home_stadium_id ?? null);
  }

  const stadiumIds = Array.from(
    new Set(Array.from(stadiumByTeam.values()).filter((s): s is string => !!s)),
  );
  const locByStadium = new Map<string, string | null>();
  if (stadiumIds.length) {
    const { data: stadiums } = await supabase
      .from("stadiums")
      .select("id, location_id")
      .in("id", stadiumIds);
    for (const s of stadiums ?? []) locByStadium.set(s.id, (s as any).location_id ?? null);
  }

  const locationIds = Array.from(
    new Set(Array.from(locByStadium.values()).filter((l): l is string => !!l)),
  );
  const coordByLocation = new Map<string, LatLng>();
  if (locationIds.length) {
    const { data: locations } = await supabase
      .from("locations")
      .select("id, latitude, longitude")
      .in("id", locationIds);
    for (const l of locations ?? []) {
      if ((l as any).latitude != null && (l as any).longitude != null) {
        coordByLocation.set(l.id, {
          lat: Number((l as any).latitude),
          lng: Number((l as any).longitude),
        });
      }
    }
  }

  for (const id of teamIds) {
    const sid = stadiumByTeam.get(id) ?? null;
    const lid = sid ? locByStadium.get(sid) ?? null : null;
    coordsByTeamId.set(id, lid ? coordByLocation.get(lid) ?? null : null);
    if (!wilayaByTeamId.has(id)) wilayaByTeamId.set(id, null);
  }
  return { coordsByTeamId, wilayaByTeamId };
}

// A small, reusable icon button component
const IconButton: React.FC<React.ComponentProps<"button">> = ({
  className,
  ...props
}) => (
  <button
    {...props}
    className={`p-1 rounded-md transition-colors ${className}`}
  />
);

export const JobRow: React.FC<JobRowProps> = React.memo(
  ({ job, onRemove, onRetry, canRetry = false, onStop, onClose }) => {
    const { t } = useTranslation("common");
    const [isRemoving, setIsRemoving] = useState(false);
    const [isRetrying, setIsRetrying] = useState(false);
    const [isOpening, setIsOpening] = useState(false);
    const [isStopping, setIsStopping] = useState(false);
    const [confirmStop, setConfirmStop] = useState(false);
    const navigate = useNavigate();
    const setCurrentSeason = useLeagueSeasonStore((s) => s.setCurrentSeason);
    const setCurrentLeagueId = useLeagueSeasonStore((s) => s.setCurrentLeagueId);

    // A finished calendar optimization saved a candidate; jump straight to that
    // group's page (Candidats view) in the right season/league context.
    const calendarGroupId: string | undefined =
      job.type === "calendar.optimize"
        ? (job.meta?.leagueGroupId as string | undefined)
        : undefined;
    // Recovery: when the launching tab died before completion, the optimize
    // result sits on the jobs row with no candidate — persist it on demand.
    // Dedup by exact pairings equality (covers the auto-saved happy path and
    // double clicks alike).
    const { showNotification } = useNotificationContext();
    const queryClient = useQueryClient();
    const [isRecovering, setIsRecovering] = useState(false);
    const [recoverDone, setRecoverDone] = useState(false);
    const saveCalendarResultAsCandidate = async () => {
      if (!calendarGroupId || isRecovering || recoverDone) return;
      setIsRecovering(true);
      try {
        const { data: j, error } = await supabase
          .from("jobs")
          .select("result, payload")
          .eq("id", job.id)
          .single();
        if (error) throw error;
        const result: any = j?.result;
        const payload: any = j?.payload;
        const pairings = result?.pairings ?? [];
        if (!pairings.length) {
          showNotification(t("job_center.calendar_recover_empty"), "error");
          return;
        }
        const { data: existing, error: exErr } = await supabase
          .from("calendar_candidates")
          .select("id, pairings")
          .eq("league_group_id", calendarGroupId);
        if (exErr) throw exErr;
        const hash = JSON.stringify(pairings);
        if ((existing ?? []).some((c: any) => JSON.stringify(c.pairings) === hash)) {
          setRecoverDone(true);
          showNotification(t("job_center.calendar_recover_exists"), "info");
          return;
        }
        const { data: g } = await supabase
          .from("league_groups")
          .select("season")
          .eq("id", calendarGroupId)
          .single();
        const season = (result?.season as string) || (g?.season as string);
        if (!season) {
          showNotification(t("job_center.calendar_recover_no_season"), "error");
          return;
        }
        const o = result?.optimization ?? {};
        const label = t("job_center.calendar_recovered_label", {
          n: (existing?.length ?? 0) + 1,
          date: new Date().toISOString().slice(0, 10),
        });

        // Candidate-quality metrics (breaks/streaks/travel/rest/coverage) so the
        // comparison table isn't blank for recovered candidates. Prefer the numbers the
        // container now returns (v21+); for older jobs that lack them, recompute
        // client-side from the pairings via the same analyzeCalendar engine so the row
        // matches a normally-saved candidate.
        let analysisMetrics: Record<string, unknown> = {};
        const hasContainerMetrics =
          typeof o.maxAwayStreak === "number" && typeof o.totalBreaks === "number";
        if (hasContainerMetrics) {
          analysisMetrics = {
            maxAwayStreak: o.maxAwayStreak,
            maxHomeStreak: o.maxHomeStreak,
            totalBreaks: o.totalBreaks,
            breaksMaxPerTeam: o.breaksMaxPerTeam,
            breaksSpread: o.breaksSpread,
            travelMax: o.travelMax ?? null,
            travelStdev: o.travelStdev ?? null,
            coverage: o.coverage,
          };
        } else {
          try {
            const teamIds = Array.from(
              new Set(
                pairings.flatMap((p: any) => [p.home_team_id, p.away_team_id]),
              ),
            ) as string[];
            const { coordsByTeamId, wilayaByTeamId } = await fetchTeamGeo(teamIds);
            const analysis = analyzeCalendar(pairings, coordsByTeamId, wilayaByTeamId, {
              legs: result?.legs ?? 2,
              roundsPerLeg: result?.rounds_per_leg ?? 0,
            });
            analysisMetrics = { ...compactCandidateMetrics(analysis) };
          } catch {
            // Geo lookup failed — save without analysis metrics rather than block recovery.
          }
        }

        await saveCalendarCandidate({
          groupId: calendarGroupId,
          season,
          label,
          source: "optimizer",
          constraints: (payload?.constraints ?? {}) as Record<string, unknown>,
          weights: (payload?.weights ?? {}) as Record<string, unknown>,
          pairings,
          metrics: {
            ...analysisMetrics,
            solverStatus: o.solver_status ?? null,
            solverWallTimeS: o.wall_time_s ?? null,
            carryOver: o.carry_over ?? null,
            carryOverCandidates: o.carry_over_candidates ?? null,
            recoveredFromJob: job.id,
          },
        });
        setRecoverDone(true);
        queryClient.invalidateQueries({ queryKey: ["calendarCandidates", calendarGroupId] });
        showNotification(t("job_center.calendar_recover_done", { label }), "success");
      } catch (e: any) {
        showNotification(e?.message ?? t("job_center.calendar_recover_failed"), "error");
      } finally {
        setIsRecovering(false);
      }
    };

    const openCalendarResult = async () => {
      if (!calendarGroupId) return;
      setIsOpening(true);
      try {
        const { data } = await supabase
          .from("league_groups")
          .select("season, league_id")
          .eq("id", calendarGroupId)
          .single();
        if (data?.season) setCurrentSeason(data.season as string);
        if (data?.league_id) setCurrentLeagueId(data.league_id as string);
        onClose?.();
        navigate(`/calendar/${calendarGroupId}`);
      } catch {
        // Best effort: still navigate with the current context.
        onClose?.();
        navigate(`/calendar/${calendarGroupId}`);
      } finally {
        setIsOpening(false);
      }
    };
    const lastPhaseRef = useRef<string | null>(null);
    const [phaseGlow, setPhaseGlow] = useState(false);

    // Phase boundary glow
    useEffect(() => {
      const current = job.phase || job.meta?.phase || null;
      if (current && lastPhaseRef.current && current !== lastPhaseRef.current) {
        // Trigger glow animation on phase boundary change
        setPhaseGlow(true);
        const timer = setTimeout(() => setPhaseGlow(false), 1200);
        return () => clearTimeout(timer);
      }
      if (!lastPhaseRef.current && current) {
        // first assignment – no animation
      }
      lastPhaseRef.current = current;
    }, [job.phase, job.meta?.phase]);

    // Track previous intra-phase value to enable smooth animated transitions.
    const lastIntraRef = useRef<number | null>(null);
    const [animateBump, setAnimateBump] = useState(false);
    // Optimistic progress simulation when backend only reports 0 then 100
    const [optimisticPct, setOptimisticPct] = useState<number | null>(null);
    const optimisticPhaseRef = useRef<string | null>(null);
    const rafRef = useRef<number | null>(null);

    useEffect(() => {
      const phase = job.phase || job.meta?.phase || null;
      const real = job.phaseProgress ?? job.meta?.phase_progress ?? null;
      // Reset when phase changes
      if (optimisticPhaseRef.current !== phase) {
        optimisticPhaseRef.current = phase;
        setOptimisticPct(null);
      }
      // If we have a real incremental value, adopt it and stop sim
      if (typeof real === "number" && real > 0 && real < 100) {
        setOptimisticPct(null);
        return;
      }
      if (phase && (real === null || real === 0)) {
        // Start / continue optimistic animation up to a soft cap (e.g., 85%)
        const start = performance.now();
        const base = optimisticPct ?? 0;
        const cap = 85; // do not exceed to avoid lying about near-completion
        const durationMs = 8000; // time to reach cap if uninterrupted
        const tick = (ts: number) => {
          const elapsed = ts - start;
          // Ease-out curve
          let ratio = Math.min(1, elapsed / durationMs);
          ratio = 1 - Math.pow(1 - ratio, 2);
          const next = Math.min(cap, base + (cap - base) * ratio);
          setOptimisticPct(next);
          if (
            next < cap &&
            (job.phaseProgress ?? job.meta?.phase_progress ?? 0) === 0 &&
            job.status === "processing"
          ) {
            rafRef.current = requestAnimationFrame(tick);
          }
        };
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
        rafRef.current = requestAnimationFrame(tick);
        return () => {
          if (rafRef.current) cancelAnimationFrame(rafRef.current);
        };
      } else {
        // If real finishes (100) or no phase, clear simulation
        setOptimisticPct(null);
      }
    }, [
      job.phase,
      job.meta?.phase,
      job.phaseProgress,
      job.meta?.phase_progress,
      job.status,
    ]);
    useEffect(() => {
      const currentIntra =
        job.phaseProgress ?? job.meta?.phase_progress ?? null;
      if (
        typeof currentIntra === "number" &&
        typeof lastIntraRef.current === "number"
      ) {
        if (currentIntra > lastIntraRef.current) {
          // Trigger a subtle bump animation to visually show progress arrival
          setAnimateBump(true);
          const t = setTimeout(() => setAnimateBump(false), 400);
          return () => clearTimeout(t);
        }
      }
      lastIntraRef.current = currentIntra;
    }, [job.phaseProgress, job.meta?.phase_progress]);
    const statusConfig = STATUS_CONFIG[job.status] || STATUS_CONFIG.pending;
    const {
      label: statusLabel,
      Icon: StatusIcon,
      color: statusColor,
    } = statusConfig;

    const { progressPct, duration, statusTooltip } = useMemo(() => {
      const progress =
        job.total && job.total > 0
          ? Math.min(100, Math.round(((job.completed || 0) / job.total) * 100))
          : job.status === "completed"
          ? 100
          : 0;

      const dur =
        job.status === "completed" || job.status === "failed"
          ? job.updatedAt - job.createdAt
          : Date.now() - job.createdAt;

      // Dispatch delay (created → started) and runtime (started → finished).
      // Surface these on the status chip so regressions in the dispatch pipeline
      // are visible at a glance.
      const parts: string[] = [];
      if (job.startedAt) {
        parts.push(`Attente: ${((job.startedAt - job.createdAt) / 1000).toFixed(1)}s`);
      }
      if (job.finishedAt && job.startedAt) {
        parts.push(`Exécution: ${((job.finishedAt - job.startedAt) / 1000).toFixed(1)}s`);
      }
      const tooltip = parts.length > 0 ? parts.join(" · ") : undefined;

      return { progressPct: progress, duration: dur, statusTooltip: tooltip };
    }, [
      job.status,
      job.total,
      job.completed,
      job.createdAt,
      job.updatedAt,
      job.startedAt,
      job.finishedAt,
    ]);

    const progressBarColor =
      job.status === "failed"
        ? "bg-red-500"
        : job.status === "completed"
        ? "bg-green-500"
        : "bg-blue-500";

    const isNew = Date.now() - job.createdAt < 3000;
    return (
      <div
        className={`px-2.5 py-2 border-b border-slate-700 last:border-b-0 transition-colors bg-[#1e293b] ${
          isNew
            ? "!bg-blue-600/30 animate-[pulse_2s_ease-in-out_2]"
            : "hover:!bg-slate-700"
        }`}
      >
        <div className="grid grid-cols-12 gap-2 items-center">
          {/* Created At */}
          <div className="col-span-2 text-xs text-white/60 tabular-nums">
            {new Date(job.createdAt).toLocaleTimeString()}
          </div>

          {/* Type, Verb & Scope */}
          <div className="col-span-4 flex flex-col gap-1 text-xs truncate">
            {(() => {
              const meta =
                JOB_KIND_META[job.type as keyof typeof JOB_KIND_META];
              const baseLabel = meta ? meta.fullLabel : job.label;
              const progressive = meta?.verbProgressive;
              const past = meta?.verbPast;
              let displayLine = baseLabel;
              if (job.status === "processing" && progressive)
                displayLine = progressive;
              else if (job.status === "completed" && past)
                displayLine =
                  past +
                  (job.meta?.generate_stats?.succeeded
                    ? ` (${job.meta.generate_stats.succeeded})`
                    : "");
              return (
                <div className="font-semibold truncate" title={displayLine}>
                  {displayLine}
                </div>
              );
            })()}
            {(() => {
              const meta =
                JOB_KIND_META[job.type as keyof typeof JOB_KIND_META];
              const subject = (job.meta as any)?.subject as
                | string
                | undefined;
              const scope = job.scope || job.meta?.scope || "";
              const tooltip = [subject, scope].filter(Boolean).join(" · ");
              return (
                <div
                  className="text-white/60 truncate flex items-center gap-2"
                  title={tooltip}
                >
                  <span
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-slate-700/70 border border-slate-600 text-white/90 text-[10px]"
                    title={meta?.fullLabel || job.type}
                  >
                    {meta?.icon && (
                      <span className="shrink-0">{meta.icon}</span>
                    )}
                    <span className="truncate max-w-[90px]">
                      {meta?.shortLabel || job.type}
                    </span>
                  </span>
                  {subject && (
                    <span className="truncate text-white/90 font-medium">
                      {subject}
                    </span>
                  )}
                  {scope && (
                    <span className="truncate text-white/50">
                      {subject ? `· ${scope}` : scope}
                    </span>
                  )}
                </div>
              );
            })()}
          </div>

          {/* Progress & Phase */}
          <div className="col-span-3 flex flex-col gap-1">
            {(() => {
              const meta =
                JOB_KIND_META[job.type as keyof typeof JOB_KIND_META];
              const phaseLabels = meta?.phaseLabels;
              const phaseWeights = meta?.phaseWeights;
              const phases = phaseLabels ? Object.keys(phaseLabels) : [];
              const currentPhase = job.phase || job.meta?.phase || null;
              const withinPhasePct =
                job.phaseProgress ?? job.meta?.phase_progress ?? null;
              if (!phaseLabels || phases.length === 0) {
                // Fallback single bar
                return (
                  <div className="h-2 bg-slate-700/50 rounded overflow-hidden">
                    <div
                      className={`h-full transition-all duration-300 ease-out ${progressBarColor}`}
                      style={{ width: `${progressPct}%` }}
                    />
                  </div>
                );
              }
              // Weighted or equal segments
              const segmentDefs = phases.map((p) => {
                const weight = phaseWeights?.find((w) => w.name === p)?.weight;
                return {
                  name: p,
                  weight:
                    typeof weight === "number" ? weight : 100 / phases.length,
                };
              });
              const totalWeight =
                segmentDefs.reduce((a, b) => a + b.weight, 0) || 1;
              const normalized = segmentDefs.map((s) => ({
                ...s,
                pct: (s.weight / totalWeight) * 100,
              }));
              const currentIdx = currentPhase
                ? phases.indexOf(currentPhase)
                : -1;
              return (
                <div
                  className={`flex h-2 w-full overflow-hidden rounded bg-slate-700/50 relative ${
                    phaseGlow
                      ? "ring-2 ring-blue-400/60 shadow-[0_0_8px_2px_rgba(96,165,250,0.6)] transition-all"
                      : "ring-0"
                  } ${animateBump ? "[animation:ping_0.6s_ease-in-out]" : ""}`}
                  role="progressbar"
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={(() => {
                    const meta =
                      JOB_KIND_META[job.type as keyof typeof JOB_KIND_META];
                    if (!meta?.phaseWeights || !job.phase) return progressPct;
                    const weights = meta.phaseWeights;
                    const total =
                      weights.reduce((a, b) => a + b.weight, 0) || 1;
                    let acc = 0;
                    for (const w of weights) {
                      if (w.name === job.phase) {
                        const intra =
                          (job.phaseProgress ?? job.meta?.phase_progress ?? 0) /
                          100;
                        acc += w.weight * intra;
                        break;
                      } else acc += w.weight;
                    }
                    return Math.min(
                      100,
                      Math.max(0, Math.round((acc / total) * 100))
                    );
                  })()}
                  aria-label={(() => {
                    const meta =
                      JOB_KIND_META[job.type as keyof typeof JOB_KIND_META];
                    const phase = job.phase || job.meta?.phase;
                    const phaseLabel = phase && meta?.phaseLabels?.[phase];
                    return phaseLabel
                      ? `${meta?.shortLabel || job.type} – ${phaseLabel}`
                      : `${meta?.shortLabel || job.type}`;
                  })()}
                >
                  {normalized.map((seg, idx) => {
                    const p = seg.name;
                    const isCompleted =
                      job.status === "completed" ||
                      idx < currentIdx ||
                      (job.status === "processing" && idx < currentIdx);
                    const isCurrent =
                      job.status === "processing" && idx === currentIdx;
                    const baseColor =
                      job.status === "failed"
                        ? "bg-red-500/60"
                        : "bg-blue-500/30";
                    const fillColor =
                      job.status === "failed"
                        ? "bg-red-500"
                        : job.status === "completed"
                        ? "bg-green-500"
                        : "bg-blue-500";
                    let innerPct = 0;
                    if (isCompleted) innerPct = 100;
                    else if (isCurrent) {
                      if (typeof withinPhasePct === "number") {
                        innerPct = Math.min(100, Math.max(0, withinPhasePct));
                      } else if (optimisticPct !== null) {
                        innerPct = optimisticPct;
                      }
                    }
                    const weightPct = Math.round(
                      (seg.weight / totalWeight) * 100
                    );
                    const displayIntra =
                      typeof withinPhasePct === "number"
                        ? withinPhasePct.toFixed(0)
                        : isCurrent && optimisticPct !== null
                        ? Math.round(optimisticPct).toString() + "~"
                        : undefined;
                    const tooltip = `${phaseLabels[p]} (${weightPct}%${
                      displayIntra ? ` – ${displayIntra}%` : ""
                    })`;
                    return (
                      <div
                        key={p}
                        className={`relative ${baseColor}`}
                        style={{ width: `${seg.pct}%` }}
                        title={tooltip}
                      >
                        <div
                          className={`h-full transition-all duration-500 ease-[cubic-bezier(.4,0,.2,1)] ${
                            innerPct > 0 ? fillColor : ""
                          } ${
                            innerPct > 0 && innerPct < 100 ? "shadow-inner" : ""
                          } ${
                            isCurrent &&
                            optimisticPct !== null &&
                            typeof withinPhasePct !== "number"
                              ? "animate-[pulse_2s_ease-in-out_infinite]"
                              : ""
                          }`}
                          style={{ width: `${innerPct}%` }}
                        >
                          {isCurrent &&
                            typeof withinPhasePct !== "number" &&
                            innerPct === 0 && (
                              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-[shimmer_1.2s_linear_infinite]" />
                            )}
                        </div>
                        {idx < normalized.length - 1 && (
                          <div className="absolute top-0 end-0 h-full w-px bg-slate-600" />
                        )}
                      </div>
                    );
                  })}
                  {phaseGlow && (
                    <div className="pointer-events-none absolute inset-0 animate-pulse bg-blue-400/10" />
                  )}
                </div>
              );
            })()}
            <div className="text-[10px] text-white/60 flex justify-between items-center">
              <span>
                {(() => {
                  // If we have weighted phases and current phase progress, derive overall weighted progress for display
                  const meta =
                    JOB_KIND_META[job.type as keyof typeof JOB_KIND_META];
                  if (!meta?.phaseWeights || !job.phase)
                    return `${progressPct}%`;
                  const weights = meta.phaseWeights;
                  const total = weights.reduce((a, b) => a + b.weight, 0) || 1;
                  let acc = 0;
                  for (const w of weights) {
                    if (w.name === job.phase) {
                      const intra =
                        (job.phaseProgress ?? job.meta?.phase_progress ?? 0) /
                        100;
                      acc += w.weight * intra;
                      break;
                    } else {
                      acc += w.weight; // completed
                    }
                  }
                  const overall = Math.min(
                    100,
                    Math.max(0, Math.round((acc / total) * 100))
                  );
                  return `${overall}%`;
                })()}
              </span>
              <span className="truncate">
                {(() => {
                  const meta =
                    JOB_KIND_META[job.type as keyof typeof JOB_KIND_META];
                  if (job.status !== "processing")
                    return formatDuration(duration);
                  const phase = job.phase || job.meta?.phase;
                  if (!meta) return formatDuration(duration);
                  const label = phase && meta.phaseLabels?.[phase];
                  return label ? label : formatDuration(duration);
                })()}
              </span>
              <span>{formatDuration(duration)}</span>
            </div>
          </div>

          {/* Status */}
          <div className="col-span-2 flex items-center gap-2 text-xs">
            <span
              className={`inline-flex items-center gap-1.5 px-2 py-1 rounded text-white font-medium ${statusColor}`}
              title={statusTooltip}
            >
              <StatusIcon
                size={14}
                className={job.status === "processing" ? "animate-spin" : ""}
              />
              {statusLabel}
            </span>
          </div>

          {/* Actions (download / retry / remove) */}
          <div className="col-span-1 flex items-center justify-end gap-1">
            {onStop && (job.status === "processing" || job.status === "pending") && job.type === "calendar.optimize" && (
              <IconButton
                disabled={isStopping}
                onClick={() => setConfirmStop(true)}
                title={t("job_center.stop_title")}
                className={`text-rose-400 hover:bg-red-500/20 ${isStopping ? "opacity-60 cursor-wait" : ""}`}
              >
                <Square size={16} className={isStopping ? "animate-pulse" : ""} />
              </IconButton>
            )}
            {(() => {
              // 1. Direct artifactUrl (legacy jobs or functions that already stored signed URL)
              if (job.artifactUrl) {
                return (
                  <a
                    href={job.artifactUrl}
                    target="_blank"
                    rel="noreferrer"
                    title="Télécharger l'artefact"
                  >
                    <IconButton className="text-blue-400 hover:bg-blue-500/20">
                      <Download size={16} />
                    </IconButton>
                  </a>
                );
              }
              // 2. Derive artifact path from payload (new unified handlers store path in payload.artifact.path)
              const artifactPath: string | undefined = job.meta?.artifact?.path;
              if (
                job.status === "completed" &&
                artifactPath &&
                (job.type === "mission_orders.bulk_pdf" ||
                  job.type === "mission_orders.single_pdf")
              ) {
                const [creating, setCreating] = React.useState(false);
                const [error, setError] = React.useState<string | null>(null);
                // Keep ephemeral signed URL in ref to avoid re-fetching within TTL window
                const urlRef = React.useRef<{
                  url: string;
                  expiresAt: number;
                } | null>(null);
                const fileName: string =
                  job.meta?.artifact?.fileName ||
                  (job.type === "mission_orders.bulk_pdf" ||
                  job.type === "mission_orders.single_pdf"
                    ? "mission_orders.pdf"
                    : "artifact");
                const bucket =
                  job.type === "mission_orders.bulk_pdf" ||
                  job.type === "mission_orders.single_pdf"
                    ? "mission_orders"
                    : undefined;
                const handleDownload = async () => {
                  if (!bucket) return;
                  setError(null);
                  try {
                    const now = Date.now();
                    if (
                      urlRef.current &&
                      urlRef.current.expiresAt > now + 3000
                    ) {
                      // Reuse valid signed URL
                      window.open(urlRef.current.url, "_blank");
                      return;
                    }
                    setCreating(true);
                    // Short TTL (60s) is enough for download; adjust if needed
                    const { data, error } = await supabase.storage
                      .from(bucket)
                      .createSignedUrl(artifactPath, 60, {
                        download: fileName,
                      });
                    if (error || !data?.signedUrl) {
                      throw new Error(
                        error?.message || "Echec création URL signée"
                      );
                    }
                    urlRef.current = {
                      url: data.signedUrl,
                      expiresAt: now + 60000,
                    };
                    window.open(data.signedUrl, "_blank");
                  } catch (e: any) {
                    setError(e?.message || "Erreur inconnue");
                  } finally {
                    setCreating(false);
                  }
                };
                return (
                  <>
                    <IconButton
                      onClick={handleDownload}
                      disabled={creating}
                      title={
                        error
                          ? `Réessayer le téléchargement (dernier échec: ${error})`
                          : `Télécharger ${fileName}`
                      }
                      className={`text-blue-400 hover:bg-blue-500/20 ${
                        creating ? "opacity-60 cursor-wait" : ""
                      }`}
                    >
                      <Download
                        size={16}
                        className={creating ? "animate-pulse" : ""}
                      />
                    </IconButton>
                  </>
                );
              }
              return null;
            })()}
            {job.status === "completed" && calendarGroupId && (
              <>
                <IconButton
                  onClick={saveCalendarResultAsCandidate}
                  disabled={isRecovering || recoverDone}
                  title={
                    recoverDone
                      ? t("job_center.calendar_recover_done_title")
                      : t("job_center.calendar_recover_title")
                  }
                  className={`text-sky-400 hover:bg-sky-500/20 ${
                    isRecovering ? "opacity-60 cursor-wait" : ""
                  } ${recoverDone ? "opacity-40" : ""}`}
                >
                  <Save size={16} className={isRecovering ? "animate-pulse" : ""} />
                </IconButton>
                <IconButton
                  onClick={openCalendarResult}
                  disabled={isOpening}
                  title={t("job_center.calendar_open_title")}
                  className={`text-emerald-400 hover:bg-emerald-500/20 ${
                    isOpening ? "opacity-60 cursor-wait" : ""
                  }`}
                >
                  <CalendarRange
                    size={16}
                    className={isOpening ? "animate-pulse" : ""}
                  />
                </IconButton>
              </>
            )}
            {canRetry && (job.status === "failed" || job.status === "cancelled") && (
              <IconButton
                disabled={isRetrying}
                onClick={async () => {
                  try {
                    setIsRetrying(true);
                    await onRetry(job.id);
                  } finally {
                    setIsRetrying(false);
                  }
                }}
                title={t("job_center.retry_title")}
                aria-label={t("job_center.retry_title")}
                className={`text-yellow-400 hover:bg-yellow-500/20 ${
                  isRetrying ? "opacity-60 cursor-wait" : ""
                }`}
              >
                <RefreshCw
                  size={16}
                  className={isRetrying ? "animate-spin" : ""}
                />
              </IconButton>
            )}
            {(job.status === "completed" ||
              job.status === "failed" ||
              job.status === "cancelled") && (
              <IconButton
                disabled={isRemoving}
                onClick={async () => {
                  try {
                    setIsRemoving(true);
                    await onRemove(job.id);
                  } finally {
                    /* keep removing state until unmounted */
                  }
                }}
                title="Supprimer"
                className={`text-rose-400 hover:bg-red-500/20 ${
                  isRemoving ? "opacity-60 cursor-wait" : ""
                }`}
              >
                <Trash2
                  size={16}
                  className={isRemoving ? "animate-pulse" : ""}
                />
              </IconButton>
            )}
          </div>
        </div>
        {/* Chunk Progress - only show for chunked jobs */}
        {(job.status === "chunked" || job.status === "merging") && (
          <div className="mt-2 px-1">
            <ChunkProgressDisplay jobId={job.id} />
          </div>
        )}
        {job.error && (
          <div
            className="mt-1.5 ps-1 text-xs text-rose-400/90 line-clamp-2 bg-red-900/20 p-2 rounded-md"
            title={job.error}
          >
            <span className="font-semibold">{t("errors.label")}:</span>{" "}
            {humanizeJobError(job.error) || job.error}
          </div>
        )}
        <ConfirmationModal
          isOpen={confirmStop}
          onClose={() => setConfirmStop(false)}
          onConfirm={async () => {
            setIsStopping(true);
            try { await onStop?.(job.id); } finally { setIsStopping(false); setConfirmStop(false); }
          }}
          title={t("job_center.stop_confirm_title")}
          message={t("job_center.stop_confirm_body")}
          confirmText={t("job_center.stop_confirm_btn")}
          variant="danger"
          isLoading={isStopping}
        />
      </div>
    );
  }
);
